import { Badge, cn } from '@design-system';
import { durationInWords, type CareScenarioSummary } from '@shared/consumerPlan';
import { CARE_ENVIRONMENT_LABELS, CARE_PHASE_COPY, CARE_SETTING_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { Figure } from './figures';

interface CareScenarioCardProps {
  scenario: CareScenarioSummary;
  isCustom: boolean;
}

/** The projected care scenario in everyday terms: when, how long, where, and what help looks like. */
export function CareScenarioCard({ scenario, isCustom }: CareScenarioCardProps) {
  const setting = scenario.careSetting
    ? CARE_SETTING_COPY[scenario.careSetting].title
    : 'Your chosen settings';

  return (
    <section
      data-testid="consumer-care-scenario"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-gray-950">
            {isCustom ? 'Your custom care scenario' : 'Your estimated care needs'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isCustom
              ? 'Your adjustments, with the same start age and length of care.'
              : 'A possible care journey based on people with a similar profile.'}
          </p>
        </div>
        <Badge variant={isCustom ? 'outline' : 'secondary'}>
          {isCustom ? 'Custom scenario' : 'Our estimate'}
        </Badge>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Figure
          label="Care may begin around"
          value={`Age ${scenario.careStartAge}`}
        />
        <Figure
          label="And may last about"
          value={durationInWords(scenario.totalMonths)}
        />
        <Figure
          label="Where you want care"
          value={setting}
        />
      </div>

      <section
        aria-label="What everyday help could look like"
        className="flex flex-col gap-3 border-t pt-6"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-gray-950">
              What everyday help could look like
            </h3>
            <p className="text-muted-foreground text-sm">
              Typical activities people need help with as needs change.
            </p>
          </div>
          <span className="text-muted-foreground text-sm tabular-nums">
            {hoursPerWeek(scenario.careHoursPerWeek)} of care on average
          </span>
        </div>
        <ol className="grid gap-3 sm:grid-cols-3">
          {scenario.phases.map((phase, index) => (
            <li
              key={phase.carePhase}
              className="flex flex-col gap-2 rounded-xl border bg-white p-4"
            >
              <div
                className="flex gap-1.5"
                aria-hidden
              >
                {scenario.phases.map((_, step) => (
                  <span
                    key={step}
                    className={cn(
                      'bg-primary/20 h-1 flex-1 rounded-full',
                      step <= index && 'bg-primary',
                    )}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between gap-2">
                <strong className="text-sm text-gray-950">
                  {CARE_PHASE_COPY[phase.carePhase].title}
                </strong>
                <span className="text-primary bg-primary/10 rounded px-1.5 py-0.5 text-xs">
                  {Math.round(phase.months)} months
                </span>
              </div>
              <span className="text-muted-foreground text-sm">
                {CARE_ENVIRONMENT_LABELS[phase.careEnvironment]} ·{' '}
                {hoursPerWeek(phase.careHoursPerWeek)}
              </span>
              <span className="text-sm text-gray-700">
                {CARE_PHASE_COPY[phase.carePhase].activities}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </section>
  );
}
